import React, { useState, useEffect, useRef, forwardRef } from "react";
import apiInstance from "../../apiInstance";
import Button from "../../Button";
import { formatDate, reformatToISO } from "../../Functions/formatDate";

const EditOrderPopup = forwardRef(
  ({ closePopup, updateOrder, initialData }, ref) => {
    const popupModalRef = useRef(null);
    useEffect(() => {
      if (ref) {
        popupModalRef.current = ref.current;
      }
    }, [ref]);

    // Form state management with initial data
    const [orderData, setOrderData] = useState(initialData);
    const [fieldErrors, setFieldErrors] = useState({});
    const lastIndexOfPayment = orderData.payments.length - 1;

    // Find selected user and widget
    const selectedUser = initialData.userId;
    const selectedWidget = initialData.widgetType;

    const handleInputChange = (e) => {
      const { name, value } = e.target;
      setOrderData((prev) => ({ ...prev, [name]: value }));
    };

    const validateForm = () => {
      const errors = {};
      if (!orderData.title?.trim()) errors.title = "Name is required";
      if (!orderData.subStatus) errors.subStatus = "Status is required";
      return errors;
    };

    const updateExistingOrder = async () => {
      setFieldErrors({});
      const errors = validateForm();
      if (Object.keys(errors).length > 0) return setFieldErrors(errors);

      try {
        const updatePayload = {
          widget: {
            title: orderData.title,
            subStatus: orderData.subStatus,
            widgetType: orderData.widgetType._id || orderData.widgetType.id,
            expires: orderData.expires,
          },
        };

        const response = await apiInstance.put(
          `/widget/${orderData._id}`,
          updatePayload
        );
        updateOrder(response.data.widget);
        closePopup();
      } catch (error) {
        if (error.response?.status === 400) {
          setFieldErrors(error.response.data.fieldErrors || {});
        } else {
          console.error("Order update failed:", error);
        }
      }
    };
    useEffect(() => {
      setOrderData(initialData);
    }, [initialData]);

    return (
      <div className="mjwidev-manual-order-pp mjwidev-pp">
        <h2>Edit Order</h2>

        <form className="mjwi-row">
          <div className="mjwi-col100">
            {/* Editable Fields */}
            {/* Name Field */}
            <div className="mjwidev-input-field">
              <label>Name:</label>
              <input
                type="text"
                name="title"
                value={orderData.title || ""}
                onChange={handleInputChange}
                required
              />
              {fieldErrors.title && (
                <span className="mjwi-error">{fieldErrors.title}</span>
              )}
            </div>

            {/* Status Dropdown */}
            <div className="mjwidev-input-field custom-select-wrapper">
              <label>Status:</label>
              <select
                name="subStatus"
                value={orderData.subStatus}
                onChange={handleInputChange}
                required
              >
                <option value="" >Select Status</option>
                <option value="active">Active</option>
                <option value="incomplete">Incomplete</option>
                <option value="past_due">Past Due</option>
                <option value="canceled">Canceled</option>
                <option value="expired">Expired</option>
                <option value="incomplete_expired">Incomplete Expired</option>
                <option value="unpaid">Unpaid</option>
              </select>
              <div className="custom-arrow">
                <svg
                  fill="#ffffff"
                  height="16px"
                  width="16px"
                  viewBox="0 0 330 330"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M325.607,79.393c-5.857-5.857-15.355-5.858-21.213,0.001l-139.39,139.393L25.607,79.393 
                c-5.857-5.857-15.355-5.858-21.213,0.001c-5.858,5.858-5.858,15.355,0,21.213l150.004,150c2.813,2.813,6.628,4.393,10.606,4.393 
                s7.794-1.581,10.606-4.394l149.996-150C331.465,94.749,331.465,85.251,325.607,79.393z"
                  ></path>
                </svg>
              </div>
              {fieldErrors.subStatus && (
                <span className="mjwi-error">{fieldErrors.subStatus}</span>
              )}
            </div>

            {/* Renewal Date Field */}
            <div className="mjwidev-input-field">
              <label>Renewal Date:</label>
              <input
                type="date"
                name="expires"
                value={
                  orderData.expires
                    ? reformatToISO(formatDate(orderData.expires))
                    : ""
                }
                onChange={handleInputChange}
              />
            </div>

            {/* Read-only Fields */}
            {/* Customer Display */}
            <div className="mjwidev-input-field">
              <label>Customer:</label>
              <input
                type="text"
                disabled
                value={selectedUser?.email || "Loading..."}
              />
            </div>

            {/* Type Display */}
            <div className="mjwidev-input-field">
              <label>Type:</label>
              <input
                type="text"
                disabled
                value={selectedWidget?.name || "Loading..."}
              />
            </div>

            {/* Amount Display */}
            <div className="mjwidev-input-field">
              <label>Amount:</label>
              <input
                type="text"
                disabled
                value={`$${selectedWidget?.price || 0}`}
              />
            </div>

            {/* Created At Display */}
            <div className="mjwidev-input-field">
              <label>Created At:</label>
              <input
                type="text"
                disabled
                value={
                  orderData.payments[lastIndexOfPayment]?.created
                    ? formatDate(
                        orderData.payments[lastIndexOfPayment]?.created
                      )
                    : "N/A"
                }
              />
            </div>
          </div>
        </form>

        <div className="mjwidev-bottom-row">
          <Button name="Save Changes" eventHandler={updateExistingOrder} />
          <Button name="Cancel" eventHandler={closePopup} />
        </div>
      </div>
    );
  }
);

export default EditOrderPopup;
